import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Loader2, User, Clock } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssignedTasks = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    deadline: null,
    priority: 'medium',
    assigned_to: ''
  });

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/assigned`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/assignable`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim() || !newTask.assigned_to) {
      toast.error('Bitte Titel und Empfänger eingeben');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/tasks`, {
        ...newTask,
        deadline: newTask.deadline?.toISOString()
      });
      setTasks([response.data, ...tasks]);
      setNewTask({ title: '', description: '', deadline: null, priority: 'medium', assigned_to: '' });
      setDialogOpen(false);
      toast.success(t('taskCreated'));
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const response = await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
      setTasks(tasks.map(t => t.id === task.id ? response.data : t));
      toast.success(t('taskUpdated'));
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const assignedToMe = tasks.filter(t => t.assigned_to === user?.id);
  const assignedByMe = tasks.filter(t => t.created_by === user?.id && t.assigned_to !== user?.id);

  return (
    <div className="space-y-6" data-testid="assigned-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">{t('assignedTasks')}</h2>
          <p className="text-slate-500 mt-1">{assignedToMe.length} mir zugewiesen</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#D92632] hover:bg-[#B91C26] text-white"
              data-testid="assign-task-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Aufgabe zuweisen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">Aufgabe zuweisen</DialogTitle>
              <DialogDescription>Weisen Sie einem Mitarbeiter eine neue Aufgabe zu</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t('taskTitle')}</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="z.B. Projekt-Präsentation erstellen"
                  className="mt-1"
                  data-testid="assign-task-title"
                />
              </div>
              
              <div>
                <Label>{t('taskDescription')}</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Optionale Beschreibung..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              <div>
                <Label>{t('assignTo')}</Label>
                <Select 
                  value={newTask.assigned_to} 
                  onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Mitarbeiter wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.id !== user?.id).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({t(u.role)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('deadline')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !newTask.deadline && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newTask.deadline ? format(newTask.deadline, 'dd.MM.yyyy') : 'Wählen...'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={newTask.deadline}
                        onSelect={(date) => setNewTask({ ...newTask, deadline: date })}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <Label>{t('priority')}</Label>
                  <Select 
                    value={newTask.priority} 
                    onValueChange={(v) => setNewTask({ ...newTask, priority: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">{t('high')}</SelectItem>
                      <SelectItem value="medium">{t('medium')}</SelectItem>
                      <SelectItem value="low">{t('low')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={handleCreateTask}
                  className="bg-[#D92632] hover:bg-[#B91C26]"
                  disabled={saving}
                  data-testid="save-assigned-task-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Task Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assigned To Me */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 font-[Manrope]">
              Mir zugewiesen ({assignedToMe.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : assignedToMe.length === 0 ? (
              <p className="text-slate-500 text-center py-8">{t('noData')}</p>
            ) : (
              assignedToMe.map((task) => (
                <div 
                  key={task.id}
                  className="p-4 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                  data-testid={`assigned-task-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{task.title}</p>
                      {task.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className={getPriorityColor(task.priority)}>
                          {t(task.priority)}
                        </Badge>
                        <Badge className={getStatusColor(task.status)}>
                          {t(task.status === 'in_progress' ? 'inProgress' : task.status)}
                        </Badge>
                        {task.deadline && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(task.deadline), 'dd.MM.yyyy')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Von: {task.created_by_name}
                      </p>
                    </div>
                    
                    <Select 
                      value={task.status} 
                      onValueChange={(v) => handleStatusChange(task, v)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('pending')}</SelectItem>
                        <SelectItem value="in_progress">{t('inProgress')}</SelectItem>
                        <SelectItem value="completed">{t('completed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Assigned By Me */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 font-[Manrope]">
              Von mir zugewiesen ({assignedByMe.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {assignedByMe.length === 0 ? (
              <p className="text-slate-500 text-center py-8">{t('noData')}</p>
            ) : (
              assignedByMe.map((task) => (
                <div 
                  key={task.id}
                  className="p-4 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">{task.title}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className={getPriorityColor(task.priority)}>
                        {t(task.priority)}
                      </Badge>
                      <Badge className={getStatusColor(task.status)}>
                        {t(task.status === 'in_progress' ? 'inProgress' : task.status)}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      An: {task.assigned_to_name}
                    </p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssignedTasks;
